/**
 * Emit one `redact` audit event for a TDO named explicitly.
 *
 * The app's other emitter, `mediaDetails`' `sendEvent`, reads its tdoId from
 * the OPEN editor file and returns without emitting when there is none — so
 * every surface that acts on a file it has not opened (the folder view: single
 * row and bulk) could dispatch LOG_AUDIT_EVENT all day and write nothing. This
 * one takes the id, so those surfaces can record what they did.
 *
 * The payload shape matches the editors' exactly (`action`, `tdoId`,
 * `userName`, `timestamp`) because the CSV formatter that renders an audit log
 * (`formatAuditEventsCsv`) reads those keys — an event that omits `userName`
 * lands in the log with a blank USER column.
 *
 * `userName` before `email`, in that order, because that is what the media
 * editor's emitter writes. The two are different fields of the user object and
 * the app treats them as such; taking the other one would interleave two
 * formats down a single file's USER column, now that a video file can be
 * changed from the folder view (through here) and from its editor (through
 * there). Audit rows are immutable once emitted.
 *
 * `application: 'redact'`, always: the platform only makes events queryable by
 * `terms: [{ tdoId }]` for an application with a server-side audit schema
 * indexing tdoId, and `redact` is the one that has it. An ad-hoc application
 * name emits successfully and can never be read back.
 *
 * Best-effort, like every audit write — enforced here rather than asserted: the
 * returned promise carries its own `catch`, so a failing audit endpoint during
 * a fifty-file bulk status change produces one log line per file and no
 * unhandled rejections. Callers may still await it.
 */
import { ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import { modules } from '@veritone/glc-redux';

import callGraphQLApi from '@helpers/callGraphQLApi';

const { config: configModule, user: userModule } = modules;

/** The only application bucket whose events are queryable by tdoId. */
export const AUDIT_APPLICATION = 'redact';

const DEFAULT_ACTION_TYPES = [
  'redact/audit/EMIT_INIT',
  'redact/audit/EMIT_SUCCESS',
  'redact/audit/EMIT_FAILURE',
] as const;

const emitAuditEventMutation = `
  mutation emitTdoAuditEvent($payload: JSONData!, $application: String) {
    emitAuditEvent(input: { application: $application, payload: $payload }) {
      id
    }
  }
`;

export const emitTdoAuditEvent =
  (
    tdoId: string,
    action: string,
    payload?: Record<string, unknown>,
    /** Override the dispatch breadcrumbs; callers with their own constants. */
    actionTypes: readonly [string, string, string] = DEFAULT_ACTION_TYPES
  ): ThunkAction<Promise<unknown>, unknown, unknown, UnknownAction> =>
  (dispatch, getState) => {
    // The glc-redux modules type their selectors against their own slices, and
    // the store's own state type is not expressible here (this helper serves
    // both apps' stores), so the read is loosened once, at the boundary.
    const state = getState() as never;
    const { eventSchemaId } = configModule.getConfig<Window['config']>(state);
    // No audit schema provisioned in this environment — emitting would write
    // events nothing can read back.
    if (!eventSchemaId || !tdoId || !action) {
      return Promise.resolve();
    }

    const eventPayload = {
      action,
      tdoId,
      userName:
        userModule.selectUser(state)?.userName ||
        userModule.selectUser(state)?.email ||
        '',
      timestamp: new Date().toISOString(),
      ...payload,
    };

    return callGraphQLApi<{ emitAuditEvent: { id: string } }>({
      actionTypes: actionTypes as unknown as [string, string, string],
      query: emitAuditEventMutation,
      variables: { payload: eventPayload, application: AUDIT_APPLICATION },
      dispatch,
      getState,
      bailout: undefined,
    }).catch(() => {
      // Fixed string: the rejection carries a server-authored message.
      console.error('audit: event not emitted', { tdoId });
      return undefined;
    });
  };

export default emitTdoAuditEvent;
